import React from 'react';
import { Upload, X } from 'lucide-react';
import { VISIBILITY_OPTIONS } from './vaultConstants';
import { formatFileSize } from './vaultUtils';

export function UploadDocumentModal({
  uploadForm,
  setUploadForm,
  documentTypes,
  uploading,
  onSubmit,
  onClose,
  onFileChange,
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="upload-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Upload Document</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document Title *</label>
            <input type="text" value={uploadForm.title} onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" placeholder="e.g., Family Will 2024" required data-testid="upload-title-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
            <select value={uploadForm.document_type} onChange={(e) => setUploadForm(prev => ({ ...prev, document_type: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" data-testid="upload-type-select">
              {documentTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Who can view this document?</label>
            <div className="space-y-2">
              {VISIBILITY_OPTIONS.map(opt => {
                const Icon = opt.icon;
                return (
                  <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${uploadForm.visibility === opt.value ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="visibility" value={opt.value} checked={uploadForm.visibility === opt.value} onChange={(e) => setUploadForm(prev => ({ ...prev, visibility: e.target.value }))} className="sr-only" />
                    <Icon className={`w-5 h-5 ${uploadForm.visibility === opt.value ? 'text-teal-600' : 'text-gray-400'}`} />
                    <div>
                      <p className="font-medium text-gray-900">{opt.label}</p>
                      <p className="text-xs text-gray-500">{opt.description}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea value={uploadForm.description} onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" rows={2} placeholder="Add notes..." data-testid="upload-description-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">File *</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-teal-400 transition-colors">
              <input type="file" onChange={onFileChange} accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" className="hidden" id="file-upload" data-testid="upload-file-input" />
              <label htmlFor="file-upload" className="cursor-pointer">
                {uploadForm.file ? (
                  <div className="text-sm"><p className="font-medium text-teal-600">{uploadForm.file.name}</p><p className="text-gray-500">{formatFileSize(uploadForm.file.size)}</p></div>
                ) : (
                  <div className="text-sm text-gray-500"><Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" /><p>Click to upload</p><p className="text-xs mt-1">PDF, Images, Word docs (max 25MB)</p></div>
                )}
              </label>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="submit" disabled={uploading || !uploadForm.file || !uploadForm.title.trim()} className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-testid="upload-submit-btn">{uploading ? 'Uploading...' : 'Upload'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
